import React, { FC, useState } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { HiOutlineCalendarDays, HiOutlinePresentationChartLine, HiOutlineMap } from 'react-icons/hi2';
import { Card } from '../../ui/Card';

export const getCountriesData = (bookings: any) => {
  if (!bookings) return null;
  const countriesMap = new Map<string, number>();

  bookings.forEach((booking: any) => {
    const country = (booking.country_of_residence || 'Unknown').toLowerCase();
    if (countriesMap.has(country)) {
      countriesMap.set(country, countriesMap.get(country)! + 1);
    } else {
      countriesMap.set(country, 1);
    }
  });

  const countries = Array.from(countriesMap).map(([country, bkg]) => ({
    country: country.charAt(0).toUpperCase() + country.slice(1),
    bkg,
  }));

  // Sort by number of bookings descending
  return countries.sort((a, b) => b.bkg - a.bkg);
};

const getBookingsByMonth = (bookings: any, year: number) => {
  if (!bookings || year > new Date().getFullYear()) return null;

  const monthsMap = new Map<string, number>();

  const monthsShortNames = Array.from({ length: 12 }, (_, monthIndex) =>
    new Date(year, monthIndex, 1).toLocaleString('default', { month: 'short' }),
  );
  
  monthsShortNames.forEach((month) => {
    monthsMap.set(month, 0);
  });

  bookings.forEach((booking: any) => {
    const createdAt = new Date(booking.createdAt);
    if (createdAt.getFullYear() === year) {
      const month = createdAt.toLocaleString('default', { month: 'short' });

      if (monthsMap.has(month)) {
        monthsMap.set(month, monthsMap.get(month)! + 1);
      } else {
        monthsMap.set(month, 1);
      }
    }
  });

  return Array.from(monthsMap).map(([month, bkgs]) => ({
    month,
    bkgs,
  }));
};

const getYearsCovered = (bookings: any): number[] => {
  const yearsSet = new Set<number>();
  if (!bookings || bookings.length === 0) {
    yearsSet.add(new Date().getFullYear());
  } else {
    bookings.forEach((booking: any) => {
      const createdAt = new Date(booking.createdAt);
      yearsSet.add(createdAt.getFullYear());
    });
  }
  return Array.from(yearsSet).sort((a, b) => b - a);
};

interface ChartsProps {
  data: any;
}

const Charts: FC<ChartsProps> = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  
  const countries = (getCountriesData(data) || []).slice(0, 10);
  const months = getBookingsByMonth(data, selectedYear) || [];
  const yearsCovered = getYearsCovered(data);
  
  // Calculate dynamic height for country chart
  const countryChartHeight = Math.max(320, countries.length * 35);

  const chartTheme = {
    mode: 'light', // This should ideally follow the theme context
    primary: '#10B981',
    secondary: '#3B82F6',
    tertiary: '#F59E0B',
    neutral: '#94A3B8'
  };

  const options: ApexOptions = {
    chart: {
      id: 'Bookings',
      toolbar: { show: false },
      fontFamily: 'inherit',
    },
    xaxis: {
      categories: months.map(m => m.month),
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: chartTheme.neutral }
      }
    },
    grid: {
      borderColor: '#F1F5F9',
      strokeDashArray: 4,
    },
    colors: [chartTheme.primary],
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '60%',
      }
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: 'smooth' as const,
      width: 3,
    },
    tooltip: {
      theme: 'dark',
    }
  };

  const optionsCountries: ApexOptions = {
    ...options,
    chart: {
      ...options.chart,
      id: 'BookingsByCountries',
      animations: { enabled: true }
    },
    xaxis: {
      ...options.xaxis,
      categories: countries.map((country: any) => country.country),
      labels: { style: { colors: chartTheme.neutral } }
    },
    yaxis: {
      labels: {
        style: { 
          colors: chartTheme.neutral,
          fontSize: '11px',
          fontWeight: 500
        },
        padding: 15,
      }
    },
    colors: [chartTheme.primary],
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
        barHeight: '55%',
        distributed: false,
      }
    },
    grid: {
      ...options.grid,
      padding: { left: 10 }
    },
    legend: { show: false }
  };

  const series = [
    {
      name: 'Bookings',
      data: months.map((month: any) => month.bkgs),
    },
  ];

  const seriesCountries = [
    {
      name: 'Bookings',
      data: countries.map((country: any) => country.bkg),
    },
  ];

  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
      {/* Monthly Bookings Chart */}
      <Card variant='glass' className='p-6'>
        <div className='flex flex-wrap items-center justify-between gap-4 mb-6'>
          <div className='flex items-center gap-3'>
            <div className='p-2 bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400 rounded-xl'>
              <HiOutlinePresentationChartLine className='w-5 h-5' />
            </div>
            <div>
              <h3 className='text-lg font-bold text-gray-900 dark:text-white'>
                Booking Performance
              </h3>
              <p className='text-xs text-gray-500 dark:text-gray-400 font-medium'>
                Monthly trends for {selectedYear}
              </p>
            </div>
          </div>
          
          <div className='relative'>
            <div className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none'>
              <HiOutlineCalendarDays className='w-4 h-4' />
            </div>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(+e.target.value)}
              className='
                pl-9 pr-8 py-1.5 text-sm font-semibold
                bg-gray-50 dark:bg-gray-800/50 
                border border-gray-200 dark:border-gray-700
                rounded-xl appearance-none cursor-pointer
                focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                transition-all duration-200
              '
            >
              {yearsCovered.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className='min-h-[300px]'>
          <Chart options={options} series={series} type='area' height={320} />
        </div>
      </Card>

      {/* Geography Chart */}
      <Card variant='glass' className='p-6'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='p-2 bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400 rounded-xl'>
            <HiOutlineMap className='w-5 h-5' />
          </div>
          <div>
            <h3 className='text-lg font-bold text-gray-900 dark:text-white'>
              Top Destinations
            </h3>
            <p className='text-xs text-gray-500 dark:text-gray-400 font-medium'>
              Top 10 booking sources
            </p>
          </div>
        </div>
        
        <div className='min-h-[300px]'>
          <Chart 
            options={optionsCountries} 
            series={seriesCountries} 
            type='bar' 
            height={countryChartHeight} 
          />
        </div>
      </Card>
    </div>
  );
};

export default Charts;
